import React, { forwardRef, useImperativeHandle, useRef, useState, useMemo, useCallback, useEffect } from "react";
import { Bold, Italic, Underline, Strikethrough, Code, List, ListOrdered } from "lucide-react";
import { searchShortcodes } from "@/components/ChatComposerKit";

// ---------------------------------------------------------------------------
// WYSIWYG chat composer. Formatting shows inline (no visible markers); on send
// the DOM is serialized to the same markdown the renderer already understands
// (**bold**, *italic*, ++underline++, ~~strike~~, `code`, - / 1. lists) so
// stored messages + every other surface stay identical. Keeps @mention and
// :shortcode: autocomplete working inside the rich editor.
// ---------------------------------------------------------------------------

function serializeNode(n) {
  if (n.nodeType === Node.TEXT_NODE) return n.nodeValue || "";
  if (n.nodeType !== Node.ELEMENT_NODE) return "";
  const tag = n.tagName.toLowerCase();
  if (tag === "br") return "\n";
  if (n.dataset && n.dataset.mentionId) return n.textContent || "";
  const inner = Array.from(n.childNodes).map(serializeNode).join("");
  switch (tag) {
    case "b": case "strong": return inner ? `**${inner}**` : "";
    case "i": case "em": return inner ? `*${inner}*` : "";
    case "u": return inner ? `++${inner}++` : "";
    case "s": case "strike": case "del": return inner ? `~~${inner}~~` : "";
    case "code": return inner ? "`" + inner + "`" : "";
    case "ul": return Array.from(n.children).map((li) => `- ${Array.from(li.childNodes).map(serializeNode).join("")}`).join("\n");
    case "ol": return Array.from(n.children).map((li, i) => `${i + 1}. ${Array.from(li.childNodes).map(serializeNode).join("")}`).join("\n");
    case "li": return inner;
    case "div": case "p": return "\n" + inner;
    default: return inner;
  }
}
function serializeEditor(root) {
  let md = Array.from(root.childNodes).map(serializeNode).join("");
  return md.replace(/\n{3,}/g, "\n\n").replace(/^\n+/, "").replace(/\s+$/,"");
}

const TOOLS = [
  { kind: "bold", cmd: "bold", Icon: Bold, title: "Bold  (Ctrl/⌘B)" },
  { kind: "italic", cmd: "italic", Icon: Italic, title: "Italic  (Ctrl/⌘I)" },
  { kind: "underline", cmd: "underline", Icon: Underline, title: "Underline  (Ctrl/⌘U)" },
  { kind: "strike", cmd: "strikeThrough", Icon: Strikethrough, title: "Strikethrough" },
  { kind: "code", cmd: "code", Icon: Code, title: "Code" },
  { kind: "bullet", cmd: "insertUnorderedList", Icon: List, title: "Bulleted list" },
  { kind: "ordered", cmd: "insertOrderedList", Icon: ListOrdered, title: "Numbered list" },
];

const RichTextInput = forwardRef(function RichTextInput(
  { users = [], onChange, onSubmit, onPasteFiles, onTyping, placeholder = "Message…", disabled = false, size = "sm", testIdPrefix = "chat", inputTestId }, ref
) {
  const edRef = useRef(null);
  const [empty, setEmpty] = useState(true);
  // Autocomplete state
  const [menu, setMenu] = useState(null); // { kind:'mention'|'shortcode', query, index, range }
  const savedRange = useRef(null);

  const emit = useCallback(() => {
    const el = edRef.current;
    if (!el) return;
    const md = serializeEditor(el);
    setEmpty(md.trim() === "");
    if (onChange) onChange(md);
    if (onTyping) onTyping(md.trim() !== "");
  }, [onChange, onTyping]);

  useImperativeHandle(ref, () => ({
    focus: () => edRef.current?.focus(),
    clear: () => { if (edRef.current) { edRef.current.innerHTML = ""; setEmpty(true); setMenu(null); if (onChange) onChange(""); } },
    getMarkdown: () => (edRef.current ? serializeEditor(edRef.current) : ""),
    getMentionIds: () => {
      const el = edRef.current; if (!el) return [];
      return Array.from(el.querySelectorAll("[data-mention-id]")).map((s) => s.dataset.mentionId);
    },
    insertText: (str) => { edRef.current?.focus(); document.execCommand("insertText", false, str); emit(); },
    isEmpty: () => (edRef.current ? serializeEditor(edRef.current).trim() === "" : true),
  }), [emit, onChange]);

  const exec = (cmd) => {
    const el = edRef.current; if (!el) return;
    el.focus();
    try { document.execCommand("styleWithCSS", false, false); } catch (e) { /* noop */ }
    if (cmd === "code") {
      const sel = window.getSelection();
      if (sel && sel.rangeCount && !sel.isCollapsed) {
        const range = sel.getRangeAt(0);
        const code = document.createElement("code");
        try { range.surroundContents(code); } catch (e) { document.execCommand("insertText", false, sel.toString()); }
        sel.removeAllRanges();
      }
    } else {
      document.execCommand(cmd, false, null);
    }
    emit();
  };

  // ---- autocomplete detection ----
  const detect = useCallback(() => {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) { setMenu(null); return; }
    const range = sel.getRangeAt(0);
    const node = range.startContainer;
    if (node.nodeType !== Node.TEXT_NODE) { setMenu(null); return; }
    const textBefore = (node.nodeValue || "").slice(0, range.startOffset);
    const mMention = textBefore.match(/(^|\s)@(\w*)$/);
    const mShort = textBefore.match(/(^|\s):([a-z0-9_+-]{2,})$/i);
    if (mMention) setMenu({ kind: "mention", query: mMention[2].toLowerCase(), index: 0, node, offset: range.startOffset, trigLen: mMention[2].length + 1 });
    else if (mShort) setMenu({ kind: "shortcode", query: mShort[2].toLowerCase(), index: 0, node, offset: range.startOffset, trigLen: mShort[2].length + 1 });
    else setMenu(null);
  }, []);

  const mentionItems = useMemo(() => {
    if (!menu || menu.kind !== "mention") return [];
    const q = menu.query;
    return (users || []).filter((u) => !q || (u.name || "").toLowerCase().includes(q)).slice(0, 6);
  }, [menu, users]);
  const shortItems = useMemo(() => (menu && menu.kind === "shortcode" ? searchShortcodes(menu.query, 8) : []), [menu]);
  const items = menu?.kind === "mention" ? mentionItems : shortItems;

  const replaceTrigger = (menuState, insertFn) => {
    const el = edRef.current; if (!el || !menuState) return;
    const { node, offset, trigLen } = menuState;
    try {
      const r = document.createRange();
      r.setStart(node, Math.max(0, offset - trigLen));
      r.setEnd(node, offset);
      r.deleteContents();
      const sel = window.getSelection();
      sel.removeAllRanges(); sel.addRange(r);
      insertFn(r, sel);
    } catch (e) { /* noop */ }
    setMenu(null);
    emit();
  };

  const pickMention = (u) => {
    replaceTrigger(menu, (r, sel) => {
      const chip = document.createElement("span");
      chip.dataset.mentionId = u.id;
      chip.contentEditable = "false";
      chip.className = "px-1 rounded-sm font-medium bg-blue-50 text-blue-700";
      chip.textContent = `@${u.name}`;
      const space = document.createTextNode("\u00A0");
      r.insertNode(space);
      r.insertNode(chip);
      // place caret after the space
      const nr = document.createRange();
      nr.setStartAfter(space); nr.collapse(true);
      sel.removeAllRanges(); sel.addRange(nr);
    });
  };
  const pickShort = (e) => {
    replaceTrigger(menu, (r, sel) => {
      const tn = document.createTextNode(e.emoji);
      r.insertNode(tn);
      const nr = document.createRange();
      nr.setStartAfter(tn); nr.collapse(true);
      sel.removeAllRanges(); sel.addRange(nr);
    });
  };
  const pick = (it) => { if (menu?.kind === "mention") pickMention(it); else pickShort(it); };

  const onKeyDown = (e) => {
    if (menu && items.length > 0) {
      if (e.key === "ArrowDown") { e.preventDefault(); setMenu((m) => ({ ...m, index: Math.min(items.length - 1, m.index + 1) })); return; }
      if (e.key === "ArrowUp") { e.preventDefault(); setMenu((m) => ({ ...m, index: Math.max(0, m.index - 1) })); return; }
      if (e.key === "Escape") { e.preventDefault(); setMenu(null); return; }
      if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); pick(items[menu.index]); return; }
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (onSubmit) onSubmit();
      return;
    }
    // Native Ctrl/Cmd+B/I/U are handled by the browser in contentEditable.
  };

  const onPaste = (e) => {
    const dt = e.clipboardData;
    if (!dt) return;
    let files = Array.from(dt.files || []);
    if (!files.length && dt.items) files = Array.from(dt.items).filter((i) => i.kind === "file").map((i) => i.getAsFile()).filter(Boolean);
    if (files.length && onPasteFiles) { e.preventDefault(); onPasteFiles(files); return; }
    // Paste as plain text to avoid foreign rich styling.
    e.preventDefault();
    const text = dt.getData("text/plain");
    document.execCommand("insertText", false, text);
  };

  useEffect(() => { try { document.execCommand("styleWithCSS", false, false); } catch (e) { /* noop */ } }, []);

  const btn = size === "xs" ? "h-6 w-6" : "h-7 w-7";
  const icon = size === "xs" ? 12 : 14;
  const minH = size === "xs" ? "min-h-[36px]" : "min-h-[40px]";

  return (
    <div className="w-full">
      <div className="flex items-center gap-0.5 pb-1" data-testid={`${testIdPrefix}-fmt-toolbar`}>
        {TOOLS.map(({ kind, cmd, Icon, title }) => (
          <button
            key={kind}
            type="button"
            title={title}
            disabled={disabled}
            onMouseDown={(e) => { e.preventDefault(); exec(cmd); }}
            className={`${btn} flex items-center justify-center rounded-sm text-slate-500 hover:bg-slate-100 hover:text-[#F54800] transition-colors disabled:opacity-40`}
            data-testid={`${testIdPrefix}-fmt-${kind}`}
          >
            <Icon size={icon} />
          </button>
        ))}
      </div>
      <div className="relative">
        {menu && items.length > 0 && (
          <div className="absolute bottom-full left-0 mb-2 w-72 max-h-64 overflow-y-auto bg-white border border-slate-200 shadow-lg rounded-sm overflow-hidden z-30" data-testid={menu.kind === "mention" ? "mention-dropdown" : "shortcode-dropdown"}>
            <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">{menu.kind === "mention" ? "Tag a teammate" : "Emoji"}</div>
            {items.map((it, i) => (
              <button
                key={menu.kind === "mention" ? it.id : it.slug}
                type="button"
                onMouseDown={(ev) => { ev.preventDefault(); pick(it); }}
                className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 ${i === menu.index ? "bg-[#F54800]/10" : "hover:bg-slate-50"}`}
                data-testid={menu.kind === "mention" ? `mention-item-${it.id}` : `shortcode-item-${it.slug}`}
              >
                {menu.kind === "mention" ? (
                  <>
                    <span className="w-6 h-6 rounded-full bg-slate-200 text-slate-700 flex items-center justify-center text-[10px] font-semibold">{(it.name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("")}</span>
                    <span className="font-medium text-slate-900">{it.name}</span>
                    <span className="text-[10px] text-slate-400 ml-auto capitalize">{it.role}</span>
                  </>
                ) : (
                  <>
                    <span className="text-lg leading-none">{it.emoji}</span>
                    <span className="text-slate-700">:{it.slug}:</span>
                  </>
                )}
              </button>
            ))}
          </div>
        )}
        <div
          ref={edRef}
          contentEditable={!disabled}
          spellCheck={true}
          suppressContentEditableWarning
          onInput={() => { emit(); detect(); }}
          onKeyUp={detect}
          onMouseUp={detect}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          className={`chat-body rich-input ${minH} max-h-40 overflow-y-auto w-full rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm leading-5 focus:outline-none focus:ring-2 focus:ring-slate-400/40`}
          data-testid={inputTestId || `${testIdPrefix}-input`}
        />
        {empty && (
          <div className="pointer-events-none absolute left-3 top-2 text-sm text-slate-400 select-none">{placeholder}</div>
        )}
      </div>
    </div>
  );
});

export default RichTextInput;
